import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { useAnalytics } from '../context/AnalyticsContext'
import { useNewLikes } from '../context/NewLikesContext'
import { useReadChats } from '../context/ReadChatsContext'
import { supabase } from '../lib/supabase'
import { saveOnboardingToProfile } from '../lib/onboarding'
import { getOnboarding } from '../pages/onboarding/onboardingStorage'
import People from '../components/People'
import Chats from '../components/Chats'
import Likes from '../components/Likes'
import Profile from '../components/Profile'
import BottomNav from '../components/BottomNav'
import Loader from '../components/Loader'
import './PersonalArea.css'

const PersonalArea = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, profile, loading, refreshProfile } = useAuth()
  const { trackEvent } = useAnalytics()
  const { hasNew, refresh } = useNewLikes()
  const { hasUnreadMessages } = useReadChats()
  const [isEditing, setIsEditing] = useState(false)
  const [showLoading, setShowLoading] = useState(true)
  /** После первого успешного показа приложения не прячем весь /app из‑за кратковременного loading */
  const [appShellReady, setAppShellReady] = useState(false)
  const lastVisibilityRefetch = useRef(0)

  const pathTab = location.pathname.replace(/^\/app\/?/, '') || 'people'
  const validTab = ['people', 'chats', 'likes', 'profile'].includes(pathTab) ? pathTab : 'people'
  const activeTab = validTab

  // Редирект /app → нужная вкладка (старые письма: /app?tab=likes) или /app/people
  useEffect(() => {
    if (location.pathname !== '/app') return
    const tab = new URLSearchParams(location.search).get('tab')
    if (tab && ['people', 'chats', 'likes', 'profile'].includes(tab)) {
      navigate(`/app/${tab}`, { replace: true })
      return
    }
    navigate('/app/people', { replace: true })
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    const checkAuth = async () => {
      if (loading) return
      
      if (!user) {
        // Короткая пауза, чтобы auth state успел обновиться после редиректа
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // Проверяем сессию напрямую
        const { data: sessionData } = await supabase.auth.getSession()
        
        if (sessionData?.session?.user) {
          console.log('[PersonalArea] Session found but user state not updated yet')
          // Сессия есть, но состояние еще не обновилось - ждем
          return
        }
        
        console.log('[PersonalArea] No user and no session, redirecting to /')
        navigate('/', { replace: true })
      }
    }
    
    checkAuth()
  }, [user, loading, navigate])

  useEffect(() => {
    if (user?.id && activeTab !== 'likes') {
      refresh(user.id)
    }
  }, [user?.id, activeTab, refresh])

  useEffect(() => {
    if (!user) setAppShellReady(false)
  }, [user])

  useEffect(() => {
    if (
      user &&
      profile?.name &&
      profile?.gender &&
      profile?.looking_for &&
      !loading
    ) {
      setAppShellReady(true)
    }
  }, [user, profile?.name, profile?.gender, profile?.looking_for, loading])

  // При возврате на вкладку — один повторный запрос профиля (в фоне браузер тормозит запросы)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      if (!user?.id || !refreshProfile) return
      const waiting = loading || (showLoading && (!profile || !profile.name))
      if (!waiting) return
      if (Date.now() - lastVisibilityRefetch.current < 2000) return
      lastVisibilityRefetch.current = Date.now()
      refreshProfile()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [user?.id, loading, profile, showLoading, refreshProfile])

  // Сохраняем профиль если есть pending данные
  useEffect(() => {
    if (user && !loading && !profile) {
      // Проверяем pending данные (fallback на случай ошибки при сохранении)
      try {
        const pendingData = localStorage.getItem('astromatch_pending_profile_save')
        if (pendingData) {
          const { userId, data, timestamp } = JSON.parse(pendingData)
          if (userId === user.id && timestamp && Date.now() - timestamp < 5 * 60 * 1000) {
            console.log('[PersonalArea] Found pending profile save, executing...')
            localStorage.removeItem('astromatch_pending_profile_save')
            saveOnboardingToProfile(userId, data)
              .then(async ({ error }) => {
                if (error) {
                  console.error('[PersonalArea] Profile save error:', error)
                } else {
                  console.log('[PersonalArea] ✅ Profile saved, reloading...')
                  if (refreshProfile) {
                    await refreshProfile()
                  }
                }
              })
              .catch(err => console.error('[PersonalArea] Exception:', err))
          } else {
            localStorage.removeItem('astromatch_pending_profile_save')
          }
        }
      } catch (e) {
        console.warn('[PersonalArea] Error processing pending data:', e)
      }
    }
  }, [user, loading, profile, refreshProfile])

  // Управляем состоянием загрузки профиля
  useEffect(() => {
    console.log('[PersonalArea] State update:', {
      hasUser: !!user,
      userId: user?.id,
      loading,
      hasProfile: !!profile,
      profileName: profile?.name,
      profileGender: profile?.gender,
      profileLookingFor: profile?.looking_for,
      profileIsNull: profile === null
    })

    if (user && !loading) {
      if (profile && profile.name && profile.gender && profile.looking_for) {
        // Профиль полностью загружен - показываем приложение
        console.log('[PersonalArea] ✅ Profile fully loaded, showing app')
        setShowLoading(false)
      } else if (profile === null) {
        // Профиль явно null - это ошибка
        console.error('[PersonalArea] ❌ Profile is null! User:', user.id)
        console.error('[PersonalArea] This means profile was not found in database.')
        // Пробуем обновить профиль еще раз
        if (refreshProfile) {
          console.log('[PersonalArea] Attempting to refresh profile...')
          refreshProfile()
        }
        // Показываем ошибку через 1.5 сек
        const timer = setTimeout(() => {
          console.log('[PersonalArea] Showing error screen after timeout')
          setShowLoading(false)
        }, 1500)
        return () => clearTimeout(timer)
      } else if (profile && profile.id && (!profile.name || !profile.gender || !profile.looking_for)) {
        // Профиль есть, но не полный - ждем еще немного
        console.warn('[PersonalArea] ⚠️ Profile exists but incomplete:', {
          hasId: !!profile.id,
          hasName: !!profile.name,
          hasGender: !!profile.gender,
          hasLookingFor: !!profile.looking_for,
          fullProfile: profile
        })
        const timer = setTimeout(() => {
          console.log('[PersonalArea] Profile loading timeout, refreshing...')
          if (refreshProfile) {
            refreshProfile()
          }
          // Показываем приложение даже если профиль неполный (может быть проблема с БД)
          setShowLoading(false)
        }, 1500)
        return () => clearTimeout(timer)
      } else {
        // Профиля нет - ждем загрузки
        console.log('[PersonalArea] No profile yet, waiting for loadProfile...')
        const timer = setTimeout(() => {
          console.log('[PersonalArea] Profile loading timeout after 2.5s, refreshing...')
          if (refreshProfile) {
            refreshProfile()
          }
          setShowLoading(false)
        }, 2500)
        return () => clearTimeout(timer)
      }
    }
  }, [user, loading, profile, refreshProfile])

  // Показываем загрузку если еще загружается auth или профиль (не сбрасываем весь shell после первого входа)
  if (
    !appShellReady &&
    (loading || (showLoading && user && (!profile || !profile.name)))
  ) {
    return (
      <div className="personal-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Loader text="Loading..." />
      </div>
    )
  }

  if (!user) {
    return null // Redirecting...
  }

  // Если профиль пустой (только id), показываем ошибку
  if (profile && !profile.name) {
    console.error('[PersonalArea] Profile is empty!', profile)
    return (
      <div className="personal-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
        <div>{t('personalArea.profileDataMissing')}</div>
        <button onClick={() => {
          if (refreshProfile) {
            refreshProfile()
          } else {
            window.location.reload()
          }
        }}>{t('personalArea.retry')}</button>
      </div>
    )
  }

  return (
    <div className="personal-area">
      {activeTab === 'people' && <People />}
      {activeTab === 'chats' && <Chats />}
      {activeTab === 'likes' && <Likes />}
      {activeTab === 'profile' && <Profile onEditingChange={setIsEditing} />}
      {!isEditing && <BottomNav activeTab={activeTab} hasNewLikesBadge={hasNew} hasNewChatsBadge={hasUnreadMessages} />}
    </div>
  )
}

export default PersonalArea

